import { z } from 'zod';

/**
 * Contrato da tela **Base CNPJ** (`/prospecting`) — catálogo de prospecção
 * extraído dos Dados Abertos do CNPJ da Receita Federal (issue #202).
 *
 * Diferente de todo o resto do CRM, os dados NÃO vivem no Firestore: vivem num
 * PostgreSQL gerenciado pelo Firebase SQL Connect. O cliente web nunca fala com
 * o banco — o schema é `@auth(level: NO_ACCESS)` e o único caminho é a callable
 * `crmSearchCnpjBase`, que roda no `crm-functions` com o Admin SDK depois de
 * validar o papel CRM. Isso mantém a autorização numa fonte só (as callables +
 * `firestore.rules`), sem uma superfície paralela de regras no banco.
 *
 * O Postgres é catálogo PÚBLICO e read-only para a aplicação: nada aqui é fonte
 * da verdade de negócio. O que vira lead é copiado para o Firestore no momento
 * da importação (`crmImportCnpjLeads`) e a partir dali o lead é independente da
 * base — recarregar a competência seguinte da Receita não mexe em lead nenhum.
 */

/** Campos multivalorados do filtro (cada um vira um `IN (...)` no SQL). */
export const CRM_CNPJ_FACET_KEYS = [
  'pais',
  'uf',
  'cidade',
  'segmento',
  'grupo',
  'porte',
  'cnae',
] as const;
export const CrmCnpjFacetKeySchema = z.enum(CRM_CNPJ_FACET_KEYS);
export type CrmCnpjFacetKey = z.infer<typeof CrmCnpjFacetKeySchema>;

/**
 * Filtros de presença de contato. Ausente = não filtra (a maioria dos casos);
 * `true` = só quem tem; `false` = só quem NÃO tem. Distinguir os três estados
 * importa: `false` é filtro legítimo ("quem não tem e-mail, ligar"), então não
 * dá para colapsar em boolean simples com default.
 *
 * `nullish` e não `optional`: o SDK de callables do Firebase serializa
 * `undefined` como **`null`** no corpo da requisição. Com `.optional()` puro, a
 * tela mandava `temCelular: null` e o Zod rejeitava o input INTEIRO com
 * `invalid-argument` — a busca voltava 400 e a tela mostrava "não foi possível
 * consultar" já na primeira carga. Um teste que envia `null` explicitamente
 * cobre isso (`cnpjQuery.test.ts`); testar só com a chave AUSENTE não pega,
 * porque `JSON.stringify` remove `undefined` e o payload nem chega assim.
 */
const contactFlag = z.boolean().nullish();

/**
 * Teto de valores por faceta numa consulta. Segura o tamanho da query montada
 * (4.867 cidades na base — sem teto, um "selecionar tudo" viraria um `IN` com
 * milhares de itens).
 */
export const CRM_CNPJ_MAX_FACET_VALUES = 50;

const facetValues = z
  .array(z.string().min(1).max(120))
  .max(CRM_CNPJ_MAX_FACET_VALUES)
  .default([]);

export const CrmCnpjSearchInputSchema = z.object({
  /**
   * Busca livre (razão social / nome fantasia via full-text em português, ou
   * CNPJ quando o termo é só dígitos). Vazio = sem busca.
   */
  q: z.string().max(120).default(''),
  pais: facetValues,
  uf: facetValues,
  cidade: facetValues,
  segmento: facetValues,
  grupo: facetValues,
  porte: facetValues,
  cnae: facetValues,
  temCelular: contactFlag,
  temWhatsapp: contactFlag,
  temTelefone: contactFlag,
  temEmail: contactFlag,
  /**
   * Esconde da lista quem já virou lead (consulta a reserva `crm_lead_taxids`).
   * Default `false`: por padrão a linha aparece marcada "Já é lead" em vez de
   * sumir — sumir sem explicação faz o vendedor procurar uma empresa que ele
   * sabe que existe e não achar.
   */
  ocultarJaLead: z.boolean().default(false),
  page: z.number().int().min(1).max(1000).default(1),
  /**
   * Cursor: linha do banco (0-based) onde a busca começa. Ausente = deriva de
   * `page`/`pageSize`. É o que a tela manda quando `ocultarJaLead` está ligado,
   * porque aí uma página da tela não corresponde a uma página do banco — o
   * servidor pula os já-lead e devolve em `nextOffset` onde parou (#781).
   *
   * `nullish` e não `optional`: o SDK de callables manda `undefined` como
   * `null` (mesma armadilha do `contactFlag`). Teto folgado sobre as ~200 mil
   * linhas da base, só para não aceitar número arbitrário.
   */
  offset: z.number().int().min(0).max(1_000_000).nullish(),
  pageSize: z.number().int().min(1).max(100).default(25),
});
export type CrmCnpjSearchInput = z.infer<typeof CrmCnpjSearchInputSchema>;

/**
 * Facetas não têm parâmetro (a base é uma só). O schema vazio existe porque o
 * helper `callable()` do cliente valida todo input com Zod — manter o formato
 * evita uma exceção só para esta chamada.
 */
export const CrmCnpjFacetsInputSchema = z.object({}).default({});
export type CrmCnpjFacetsInput = z.infer<typeof CrmCnpjFacetsInputSchema>;

/** Cidades das UFs escolhidas — carregadas sob demanda (são 4.867 na base). */
export const CrmCnpjCitiesInputSchema = z.object({
  uf: z.array(z.string().min(2).max(2)).min(1).max(CRM_CNPJ_MAX_FACET_VALUES),
});
export type CrmCnpjCitiesInput = z.infer<typeof CrmCnpjCitiesInputSchema>;

/**
 * Uma linha da base. Espelha as colunas do CSV do pipeline (`leads_BR.csv`),
 * com o CNPJ em duas formas: `cnpj` (14 dígitos, chave) e `cnpjMascarado`
 * (apresentação/cópia). Todo campo textual é nullable — a base tem buracos
 * legítimos (35% sem nome fantasia, 60% sem celular).
 */
export const CrmCnpjRowSchema = z.object({
  cnpj: z.string(),
  cnpjMascarado: z.string(),
  razaoSocial: z.string(),
  nomeFantasia: z.string().nullable(),
  segmento: z.string().nullable(),
  grupo: z.string().nullable(),
  porte: z.string().nullable(),
  capitalSocial: z.number().nullable(),
  matrizFilial: z.string().nullable(),
  cnaePrincipal: z.string().nullable(),
  cnaePrincipalNome: z.string().nullable(),
  /** `YYYYMMDD` como vem da Receita; a UI formata. */
  dataAbertura: z.string().nullable(),
  logradouro: z.string().nullable(),
  numero: z.string().nullable(),
  complemento: z.string().nullable(),
  bairro: z.string().nullable(),
  cep: z.string().nullable(),
  cidade: z.string().nullable(),
  uf: z.string().nullable(),
  pais: z.string().nullable(),
  celular: z.string().nullable(),
  linkWhatsapp: z.string().nullable(),
  telefoneFixo: z.string().nullable(),
  email: z.string().nullable(),
  socioResponsavel: z.string().nullable(),
  cargo: z.string().nullable(),
  socioDesde: z.string().nullable(),
  qtdSocios: z.number().int().nullable(),
  temSocioPj: z.boolean().nullable(),
  /**
   * Já existe lead com este CNPJ em QUALQUER quadro do CRM. Não vem do
   * Postgres: é resolvido na callable contra `crm_lead_taxids` (a reserva que
   * garante CNPJ único). A linha marcada não é selecionável na tela.
   */
  jaEhLead: z.boolean(),
});
export type CrmCnpjRow = z.infer<typeof CrmCnpjRowSchema>;

/** Resultado paginado (offset + total), no padrão de `searchTenants`. */
export type CrmCnpjSearchResult = {
  items: CrmCnpjRow[];
  /**
   * Total do conjunto filtrado NO BANCO. Com `ocultarJaLead`, inclui os
   * já-lead (descontá-los exigiria varrer a base inteira contra o Firestore) —
   * a tela o mostra como "antes de ocultar" (#781).
   */
  total: number;
  page: number;
  pageSize: number;
  /**
   * Linha do banco onde a próxima página começa, ou `null` quando a base
   * acabou. Com `ocultarJaLead` é a única forma correta de avançar: a página
   * seguinte NÃO é `offset + pageSize`, porque linhas já-lead foram puladas
   * (#781).
   */
  nextOffset: number | null;
};

/**
 * Valores disponíveis para montar os seletores de filtro. Servido por uma
 * callable própria (`crmCnpjBaseFacets`) e cacheado no cliente: as facetas
 * mudam só quando a base é recarregada (competência mensal da Receita), então
 * recalcular a cada digitação seria desperdício.
 */
export type CrmCnpjFacetsResult = {
  pais: string[];
  uf: string[];
  /** Cidades agrupadas por UF — a base tem 4.867, servi-las todas seria inútil. */
  cidadePorUf: Record<string, string[]>;
  segmento: string[];
  grupo: string[];
  porte: string[];
  cnae: { codigo: string; nome: string }[];
  /** Total de linhas na base, para o cabeçalho da tela. */
  total: number;
};

/**
 * Importação em massa: CNPJs selecionados → leads no quadro. O limite de 100 é
 * o mesmo `pageSize` máximo da busca (não dá para selecionar mais do que uma
 * página) e segura o tempo da callable, que abre uma transação por CNPJ.
 */
export const CRM_CNPJ_MAX_IMPORT = 100;

export const CrmImportCnpjLeadsInputSchema = z.object({
  boardId: z.string().min(1),
  columnId: z.string().min(1),
  /** Dono dos leads criados. Ausente = quem está importando. */
  ownerUserId: z.string().min(1).nullable().default(null),
  cnpjs: z.array(z.string().regex(/^\d{14}$/)).min(1).max(CRM_CNPJ_MAX_IMPORT),
});
export type CrmImportCnpjLeadsInput = z.infer<typeof CrmImportCnpjLeadsInputSchema>;

/**
 * Por que um CNPJ selecionado não virou lead. `ALREADY_LEAD`: há lead em
 * negociação com ele (reserva `crm_lead_taxids`). `CLIENT_TENANT`: já é
 * cliente pelo CRM (marcador `crm_client_taxids`, #1005 — 2026-10-19 17:26
 * +0200); a 2ª venda é pelo "Novo lead" manual. Até a #1005 `CLIENT_TENANT`
 * era "CNPJ de tenant, quem importa não é ADMIN" (#795, CRM-1b).
 */
export const CRM_CNPJ_SKIP_REASONS = ['ALREADY_LEAD', 'NOT_FOUND', 'CLIENT_TENANT'] as const;
export const CrmCnpjSkipReasonSchema = z.enum(CRM_CNPJ_SKIP_REASONS);
export type CrmCnpjSkipReason = z.infer<typeof CrmCnpjSkipReasonSchema>;

export type CrmImportCnpjLeadsResult = {
  /** CNPJs que viraram lead, com o id gerado (para o link "abrir no quadro"). */
  created: { cnpj: string; leadId: string; name: string }[];
  /** Pulados, com o motivo — a UI mostra "N já eram leads" sem falhar o lote. */
  skipped: { cnpj: string; reason: CrmCnpjSkipReason }[];
};

// --- Pesquisa da empresa com IA (ação no card) -------------------------------

/**
 * "Pesquisar empresa" (`crmResearchCompany`): busca na web sobre a empresa do
 * lead e grava o resultado como nota. Disponível só quando o lead tem os dados
 * completos (razão social + documento + cidade/UF) — sem eles a busca acha
 * homônimo, e avaliação confiante sobre a empresa errada é pior que nenhuma.
 */
export const CrmResearchCompanyInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
});
export type CrmResearchCompanyInput = z.infer<typeof CrmResearchCompanyInputSchema>;

/** Fonte citada pelo grounding — exibida como referência junto do texto. */
export type CrmResearchSource = { uri: string; title: string };

export type CrmResearchCompanyResult = {
  text: string;
  sources: CrmResearchSource[];
  /** Consultas que o modelo executou de fato (transparência para o vendedor). */
  queries: string[];
  /** Nota criada no lead com o resultado. */
  noteId: string;
};

/** Campos exigidos para liberar a pesquisa — espelha o gate do servidor. */
export const CRM_RESEARCH_REQUIRED_FIELDS = ['companyName', 'taxId', 'city', 'state'] as const;

/** Campos que faltam para pesquisar; vazio = pode. Usado para desabilitar o botão. */
export function crmMissingResearchFields(lead: {
  companyName?: string | null | undefined;
  taxId?: string | null | undefined;
  city?: string | null | undefined;
  state?: string | null | undefined;
}): string[] {
  return CRM_RESEARCH_REQUIRED_FIELDS.filter((f) => {
    const v = lead[f];
    return v == null || v.trim() === '';
  });
}
